/**
 * Settings · Procurement · T&C Library
 *
 * Procurement_7Sep2026 §7 — maintain standard Terms & Conditions clause sets
 * (backend: /tc-library) so Award / Direct PO can pick a set instead of
 * typing terms from scratch each time.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, FileText, Plus, Trash2, Star } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';

export default function TCLibrary() {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, clauses: [], is_default }
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiGet('/settings/tc-library').then((d) => setSets(d?.sets || [])).catch(() => toast.error('Failed to load T&C sets')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ name: '', clauses: [''], is_default: sets.length === 0 });
  const openEdit = (s) => setEditing({ ...s, clauses: [...(s.clauses || []), ''] });

  const save = async () => {
    const clauses = (editing.clauses || []).map((c) => c.trim()).filter(Boolean);
    if (!editing.name.trim()) { toast.error('Name is required'); return; }
    if (!clauses.length) { toast.error('Add at least one clause'); return; }
    setSaving(true);
    try {
      const payload = { name: editing.name.trim(), clauses, is_default: !!editing.is_default };
      if (editing.id) await apiPut(`/settings/tc-library/${editing.id}`, payload);
      else await apiPost('/settings/tc-library', payload);
      toast.success('T&C set saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await apiDel(`/settings/tc-library/${s.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="tc-library-page">
      <PageHeader title="Procurement · T&C Library" subtitle="Standard Terms & Conditions clause sets, picked when awarding an RFQ or raising a Direct PO." />
      <div className="max-w-3xl mt-6 space-y-4">
        <button onClick={openNew} data-testid="tc-new-set-btn" className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#3b4872] text-white rounded-lg">
          <Plus className="w-4 h-4" /> New T&C Set
        </button>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : sets.length === 0 ? (
          <p className="text-sm text-slate-400">No T&C sets yet.</p>
        ) : sets.map((s) => (
          <div key={s.id} className="bg-white border rounded-lg p-4" data-testid={`tc-set-${s.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-display font-bold text-slate-800">{s.name}</span>
                {s.is_default && <span className="tag bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"><Star size={10} /> Default</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(s)} data-testid={`tc-edit-${s.id}`} className="text-xs text-[#3b4872] font-display font-semibold">Edit</button>
                <button onClick={() => remove(s)} data-testid={`tc-delete-${s.id}`} className="text-rose-500"><Trash2 size={14} /></button>
              </div>
            </div>
            <ol className="list-decimal list-inside text-xs text-slate-600 mt-2 space-y-1">
              {(s.clauses || []).map((c, i) => <li key={i}>{c}</li>)}
            </ol>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="tc-edit-modal">
          <div className="bg-white rounded-lg p-5 w-full max-w-lg space-y-3 max-h-[85vh] overflow-y-auto">
            <h3 className="font-display font-bold text-slate-800">{editing.id ? 'Edit' : 'New'} T&C Set</h3>
            <label className="text-xs text-slate-600 block">Name
              <input value={editing.name} onChange={(e) => setEditing((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="tc-name-input" />
            </label>
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing((f) => ({ ...f, is_default: e.target.checked }))} data-testid="tc-default-checkbox" />
              Make this the default set (auto-picked when no set is selected)
            </label>
            <div className="space-y-2">
              <label className="text-xs text-slate-600">Clauses (one per line)</label>
              {editing.clauses.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-4">{i + 1}.</span>
                  <input value={c} data-testid={`tc-clause-input-${i}`}
                    onChange={(e) => setEditing((f) => {
                      const next = [...f.clauses]; next[i] = e.target.value;
                      if (i === next.length - 1 && e.target.value.trim()) next.push('');
                      return { ...f, clauses: next };
                    })}
                    className="flex-1 border border-slate-200 rounded-sm px-3 py-1.5 text-sm" />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-2 text-slate-500" data-testid="tc-cancel-btn">Cancel</button>
              <button onClick={save} disabled={saving} className="text-xs px-4 py-2 bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1" data-testid="tc-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
